import { ref, toValue, watch, type MaybeRefOrGetter } from 'vue'

export interface UseCitiesOptions {
  /** City shown before any lookup, e.g. an existing address value. Never cleared by lookups. */
  initialCity?: string
  /** Currently selected country id; lookup is skipped for an explicitly foreign selection. */
  countryId?: MaybeRefOrGetter<string | null | undefined>
  /** Id of the Swiss country in the shop country list. A selected country with unknown Swiss id is not assumed Swiss. */
  swissCountryId?: MaybeRefOrGetter<string | null | undefined>
}

export const useCities = async (options: UseCitiesOptions = {}) => {
  const city = ref(options.initialCity ?? '')

  let latestQuery = 0
  let activeController: AbortController | null = null
  let lastAssigned = options.initialCity ?? ''
  // Newest raw input intent across both fields. A lookup may only replace the
  // city while no newer city intent owns the field.
  let lastIntent: 'zip' | 'city' | null = null
  const currentIntent = (): 'zip' | 'city' | null => lastIntent

  const invalidatePending = () => {
    ++latestQuery
    activeController?.abort()
    activeController = null
  }

  const isApplicableCountry = (): boolean => {
    const selected = toValue(options.countryId)
    if (!selected) return true
    const swiss = toValue(options.swissCountryId)
    if (!swiss) return false
    return selected === swiss
  }

  // Native capture determines edit order. FormKit may commit an older city
  // edit after a newer ZIP starts its lookup; that commit must not cancel it.
  watch(city, (value) => {
    if (value === lastAssigned) return
    lastAssigned = value
    if (currentIntent() !== 'zip') invalidatePending()
  })

  // Switching to a non-applicable country supersedes any pending lookup result.
  watch(
    () => toValue(options.countryId),
    () => {
      if (!isApplicableCountry()) {
        invalidatePending()
      }
    },
  )

  const getCity = async (code: unknown): Promise<void> => {
    const query = ++latestQuery
    activeController?.abort()
    activeController = null

    if (typeof code !== 'string' || code.length !== 4) {
      return
    }

    if (!isApplicableCountry()) {
      return
    }

    if (currentIntent() === 'city') {
      // A newer city intent owns the field; a lookup for an older ZIP value
      // must not start, even if no request existed at edit time.
      return
    }

    const controller = new AbortController()
    activeController = controller

    try {
      const response = await fetch(`/api/city/${code}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/plain',
        },
        signal: controller.signal,
      })
      if (query !== latestQuery || controller.signal.aborted) {
        return
      }
      if (!response.ok) {
        return
      }
      const name = (await response.text()).trim()
      if (query !== latestQuery || !isApplicableCountry() || currentIntent() === 'city' || !name) {
        return
      }
      lastAssigned = name
      city.value = name
    } catch {
      // Aborted, failed or unknown ZIP: keep the last city or manual entry.
    } finally {
      if (activeController === controller) {
        activeController = null
      }
    }
  }

  return {
    city,
    handleZipCodeChange: getCity,
    // Native capture listeners in the three address forms run before FormKit's
    // delayed model commit. Programmatic model updates do not fire DOM input.
    handleCityInput: (event?: Event) => {
      lastIntent = 'city'
      invalidatePending()
      // Keep the model current with native edits. Otherwise an early ZIP
      // response equal to the old model is a no-op and the delayed edit wins.
      const value = (event?.target as HTMLInputElement | null)?.value
      if (typeof value === 'string') {
        lastAssigned = value
        city.value = value
      }
    },
    handleZipCodeInput: (event?: Event) => {
      lastIntent = 'zip'
      invalidatePending()
      if (event) {
        return getCity((event.target as HTMLInputElement | null)?.value)
      }
    },
  }
}
